import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Inbox } from "lucide-react";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { fmtDate, fmtDateTime, fmtMoney } from "../../../lib/format";
import { StatusBadge } from "../../../components/common/StatusBadge";
import { DateField } from "../../../components/common/DateField";
import { EmptyState, SkeletonBlock } from "../../../components/common/EmptyState";

const STATUSES = ["in_review", "pending_approval", "submitted", "received", "prepared", "out_for_delivery", "delivered", "invoiced", "cancelled"];
const sel = "h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-navy";
const FULL_EVERY = 300000;

// Polls only the app orders that changed since the last revision; a full reload every 5 minutes picks up SoftOne-side status changes.
const useQueueRows = () => {
  const [rows, setRows] = useState(null);
  const rev = useRef(0);
  const lastFull = useRef(0);
  const pull = useCallback(async () => {
    const full = !rev.current || Date.now() - lastFull.current > FULL_EVERY;
    try {
      const { data } = await api.get("/staff/orders/changes", { params: { since: full ? 0 : rev.current } });
      if (full) lastFull.current = Date.now();
      rev.current = data.rev;
      setRows((prev) => {
        const map = new Map(full ? [] : (prev || []).map((r) => [r.local_id, r]));
        data.rows.forEach((r) => map.set(r.local_id, r));
        return [...map.values()].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
      });
    } catch (e) { setRows((p) => p || []); }
  }, []);
  useEffect(() => {
    pull();
    const id = setInterval(pull, 20000);
    window.addEventListener("vavlis:queue", pull);
    return () => { clearInterval(id); window.removeEventListener("vavlis:queue", pull); };
  }, [pull]);
  return rows;
};

export default function OrderQueue() {
  const { t } = useI18n();
  const [f, setF] = useState({ status: "", area: "", sales_rep: "", delivery_date: "" });
  const all = useQueueRows();
  const q = { isLoading: all === null, data: (all || []).filter((r) => (!f.status || r.status === f.status) && (!f.area || r.area_code === f.area)
    && (!f.sales_rep || r.sales_rep === f.sales_rep) && (!f.delivery_date || r.requested_delivery_date === f.delivery_date)) };
  const areas = [...new Map((all || []).map((r) => [r.area_code, r.area_name])).entries()];
  const reps = [...new Set((all || []).map((r) => r.sales_rep))];
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e || "" }));

  return (
    <div className="space-y-5" data-testid="order-queue">
      <div><h1 className="text-3xl font-bold text-navy">{t("ops.queueTitle")}</h1><p className="mt-1 text-sm text-slate-500">{t("ops.queueSub")}</p></div>
      <div className="flex flex-wrap items-end gap-2" data-testid="queue-filters">
        <select value={f.status} onChange={set("status")} className={sel} data-testid="queue-filter-status">
          <option value="">{t("ops.allStatuses")}</option>{STATUSES.map((s) => <option key={s} value={s}>{t(`status.${s}`)}</option>)}
        </select>
        <select value={f.area} onChange={set("area")} className={sel} data-testid="queue-filter-area">
          <option value="">{t("ops.allAreas")}</option>{areas.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
        </select>
        <select value={f.sales_rep} onChange={set("sales_rep")} className={sel} data-testid="queue-filter-rep">
          <option value="">{t("ops.allReps")}</option>{reps.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <div className="w-44"><DateField value={f.delivery_date} onChange={set("delivery_date")} testId="queue-filter-date" /></div>
        <button type="button" onClick={() => setF({ status: "", area: "", sales_rep: "", delivery_date: "" })} className="h-11 rounded-full px-4 text-sm font-medium text-slate-600 hover:bg-white" data-testid="queue-filter-clear">{t("ops.clear")}</button>
      </div>
      {q.isLoading ? <SkeletonBlock className="h-64" /> : !q.data?.length ? <EmptyState icon={Inbox} title={t("ops.empty")} testId="queue-empty" /> : (
        <ul className="divide-y divide-slate-100 overflow-hidden rounded-2xl bg-white shadow-sm" data-testid="queue-list">
          {q.data.map((r) => (
            <li key={r.local_id}>
              <Link to={`/staff/orders/${r.local_id}`} data-testid={`queue-row-${r.local_id}`} className={`flex flex-wrap items-center gap-3 px-4 py-3.5 hover:bg-slate-50 ${r.status === "in_review" ? "border-l-4 border-brand" : ""}`}>
                <div className="min-w-0 flex-1 basis-40">
                  <p className="truncate font-semibold text-navy">{r.customer_name}</p>
                  <p className="break-words text-xs text-slate-500">{r.doc_no || r.local_id.slice(0, 8)} · {fmtDateTime(r.created_at)} · {r.area_name} · {r.sales_rep}</p>
                </div>
                <div className="text-right text-xs text-slate-500">{t("ops.deliveryDate")}<p className="text-sm font-medium text-navy">{r.requested_delivery_date ? fmtDate(r.requested_delivery_date) : "—"}</p></div>
                <div className="flex max-w-[45%] shrink-0 flex-col items-end gap-1 text-right"><span className="font-semibold tabular-nums text-navy">{fmtMoney(r.gross_total)}</span><StatusBadge status={r.status} /></div>
                <ChevronRight className="h-4 w-4 text-slate-300" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
